import api from '@/lib/axios';

import type { SessionResponse } from '@/types/session';

/** Fetch current session from Gateway (GET /session, proxied via BFF /api/session) */
export const getSession = async (): Promise<SessionResponse> => {
  const response = await api.get<SessionResponse>('/session');
  return response.data;
};

/**
 * Logout via BFF (POST /logout).
 * Uses fetch directly — /logout is a BFF-level route, not behind /api gateway.
 * Avoids the axios envelope interceptor which would treat it as an API call.
 *
 * `credentials: 'include'` so the session cookie is sent cross-origin (the BFF
 * lives on a different subdomain in deployed envs). The origin comes from
 * VITE_API_BASE_URL — empty for same-origin local dev.
 *
 * Throws on non-2xx so callers can surface a failed BFF logout — without this,
 * a silently-failed POST left the SSO cookie intact and the next page render
 * re-authenticated, compounding the "logout took two clicks" symptom.
 */
export const logout = async (): Promise<void> => {
  const origin = import.meta.env.VITE_API_BASE_URL ?? '';
  const response = await fetch(`${origin}/logout`, { method: 'POST', credentials: 'include' });
  if (!response.ok) {
    throw new Error(`Logout failed with status ${response.status}`);
  }
};
